import { SAUNA_SETUP_NOTE_MAX_LENGTH } from '@jums-dashboard/shared';
import { useId, useState, type FormEvent } from 'react';

import Button from '@/components/Button/Button';
import FieldMessage from '@/features/homeAutomation/components/FieldMessage';
import { cn } from '@/lib/cn';
import { errorInputClasses, inputClasses } from '@/lib/formStyles';

import { fromDateTimeLocalValue, toDateTimeLocalValue, validateSetupNoteText } from './sessionHistory';

export interface SetupNoteFormValues {
  text: string;
  notedAt: string;
}

export interface SaunaSetupNoteFormProps {
  initialText?: string;
  initialNotedAt?: string;
  submitLabel: string;
  saving: boolean;
  onSubmit: (values: SetupNoteFormValues) => Promise<unknown>;
  onCancel?: () => void;
}

/** Add / edit form for a dated setup note. `notedAt` defaults to now and can be backdated. */
export default function SaunaSetupNoteForm({
  initialText = '',
  initialNotedAt,
  submitLabel,
  saving,
  onSubmit,
  onCancel,
}: SaunaSetupNoteFormProps) {
  const id = useId();
  const [text, setText] = useState(initialText);
  const [notedAt, setNotedAt] = useState(() => toDateTimeLocalValue(initialNotedAt ? new Date(initialNotedAt) : new Date()));
  const [touched, setTouched] = useState(false);

  const textError = validateSetupNoteText(text);
  const notedAtIso = fromDateTimeLocalValue(notedAt);
  const dateError = notedAtIso ? null : 'Enter a valid date and time.';
  const showTextError = touched && textError;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (textError || !notedAtIso) return;
    try {
      await onSubmit({ text: text.trim(), notedAt: notedAtIso });
      if (!onCancel) {
        setText('');
        setNotedAt(toDateTimeLocalValue(new Date()));
        setTouched(false);
      }
    } catch {
      // Error toast is shown by the mutation hook; keep the user's input.
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2" noValidate>
      <label className="flex flex-col gap-1 text-xs font-medium text-(--text-secondary)" htmlFor={`${id}-text`}>
        Note
        <textarea
          id={`${id}-text`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => setTouched(true)}
          rows={2}
          maxLength={SAUNA_SETUP_NOTE_MAX_LENGTH}
          placeholder="e.g. Moved the sensor 20 cm higher"
          aria-invalid={!!showTextError}
          aria-describedby={showTextError ? `${id}-text-error` : undefined}
          className={cn(inputClasses, 'h-auto py-2', showTextError && errorInputClasses)}
        />
        {showTextError && (
          <FieldMessage id={`${id}-text-error`} tone="error">
            {textError}
          </FieldMessage>
        )}
      </label>
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-xs font-medium text-(--text-secondary)" htmlFor={`${id}-date`}>
          Date
          <input
            id={`${id}-date`}
            type="datetime-local"
            value={notedAt}
            onChange={(e) => setNotedAt(e.target.value)}
            aria-invalid={!!dateError}
            aria-describedby={dateError ? `${id}-date-error` : undefined}
            className={cn(inputClasses, dateError && errorInputClasses)}
          />
        </label>
        <Button type="submit" size="md" loading={saving} disabled={saving}>
          {submitLabel}
        </Button>
        {onCancel && (
          <Button type="button" variant="ghost" size="md" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        )}
      </div>
      {dateError && (
        <FieldMessage id={`${id}-date-error`} tone="error">
          {dateError}
        </FieldMessage>
      )}
    </form>
  );
}
